import * as actions from '../actions/actionTypes';

export const MERCHANDISE_UI_FIELDS = new Set([
  'hasFetchedMetadata',
  'metadataByMerchandise',
  'expandInvoiceItem',
  'showLoader',
]);

export const defaultInvoiceUIState = () => ({
  hasFetchedMetadata: false,
  metadataByMerchandise: {},
  expandInvoiceItem: true,
  showLoader: false,
});

const initialState = {
  byInvoiceId: {},
};

function invoiceKeyFromInvoice(invoice, index) {
  const invoiceId = invoice?.invoiceId;
  if (invoiceId && invoiceId !== '') return invoiceId;
  return `idx:${index}`;
}

function mergeMetadataForMerchandise(existingMetadata, merchandiseId, merchandiseDutyId, metadataEntry) {
  if (!merchandiseId || !merchandiseDutyId || !metadataEntry) return existingMetadata;
  const existingForMerch = existingMetadata[merchandiseId] || {};
  return {
    ...existingMetadata,
    [merchandiseId]: {
      ...existingForMerch,
      [merchandiseDutyId]: metadataEntry,
    },
  };
}

function setInvoiceUIState(state, invoiceKey, patch) {
  const current = state.byInvoiceId[invoiceKey] || defaultInvoiceUIState();
  return {
    ...state,
    byInvoiceId: {
      ...state.byInvoiceId,
      [invoiceKey]: {
        ...current,
        ...patch,
      },
    },
  };
}

export default function MerchandiseUIReducer(state = initialState, action) {
  switch (action.type) {
    case actions.INITIALIZE_VALUES_DESCRIPTION_OF_MERCHANDISE: {
      const invoices = action.payload?.entrySummaryInvoices || [];
      const nextByInvoiceId = { ...state.byInvoiceId };

      invoices.forEach((invoice, index) => {
        const key = invoiceKeyFromInvoice(invoice, index);
        const existing = nextByInvoiceId[key];
        if (existing) {
          nextByInvoiceId[key] = {
            ...defaultInvoiceUIState(),
            ...existing,
          };
        } else {
          nextByInvoiceId[key] = defaultInvoiceUIState();
        }
      });

      return {
        ...state,
        byInvoiceId: nextByInvoiceId,
      };
    }

    case actions.CLEAR_INVOICE_METADATA: {
      const nextByInvoiceId = {};
      Object.keys(state.byInvoiceId).forEach((key) => {
        nextByInvoiceId[key] = {
          ...(state.byInvoiceId[key] || defaultInvoiceUIState()),
          hasFetchedMetadata: false,
          metadataByMerchandise: {},
        };
      });
      return {
        ...state,
        byInvoiceId: nextByInvoiceId,
      };
    }

    case actions.UPDATE_INVOICE_LINE_ITEM_FIELD: {
      if (!MERCHANDISE_UI_FIELDS.has(action.field)) return state;
      const invoiceKey = action.invoiceKey;
      if (!invoiceKey) return state;
      return setInvoiceUIState(state, invoiceKey, {
        [action.field]: action.payload,
      });
    }

    case actions.ADD_INVOICE_ITEM: {
      const invoice = action.newInvoiceItem;
      if (!invoice) return state;
      const key = invoiceKeyFromInvoice(invoice, Object.keys(state.byInvoiceId).length);
      if (state.byInvoiceId[key]) return state;
      return setInvoiceUIState(state, key, {
        ...defaultInvoiceUIState(),
        expandInvoiceItem: invoice.expandInvoiceItem ?? true,
        showLoader: invoice.showLoader ?? false,
      });
    }

    case actions.REMOVE_INVOICE_LINE_ITEM: {
      if (!action.invoiceId) return state;
      const nextByInvoiceId = { ...state.byInvoiceId };
      delete nextByInvoiceId[action.invoiceId];
      return {
        ...state,
        byInvoiceId: nextByInvoiceId,
      };
    }

    case 'SHOW_LOADER': {
      if (!action.invoiceKey) return state;
      return setInvoiceUIState(state, action.invoiceKey, { showLoader: true });
    }

    case 'HIDE_LOADER': {
      if (!action.invoiceKey) return state;
      return setInvoiceUIState(state, action.invoiceKey, { showLoader: false });
    }

    case 'HIDE_UNHIDE_INVOICE': {
      if (!action.invoiceKey) return state;
      return setInvoiceUIState(state, action.invoiceKey, { expandInvoiceItem: true });
    }

    case 'HIDE_INVOICE': {
      if (!action.invoiceKey) return state;
      return setInvoiceUIState(state, action.invoiceKey, { expandInvoiceItem: false });
    }

    case 'SET_HTS_ADD_DATA': {
      const invoiceKey = action.invoiceKey;
      const primaryHtsMetadata = action.payload?.data?.primaryHtsMetadata;
      const merchandiseId = action.merchandiseId;
      const merchandiseDutyId = action.merchandiseDutyId;
      if (!invoiceKey || !primaryHtsMetadata || !merchandiseId || !merchandiseDutyId) return state;

      const current = state.byInvoiceId[invoiceKey] || defaultInvoiceUIState();
      return setInvoiceUIState(state, invoiceKey, {
        metadataByMerchandise: mergeMetadataForMerchandise(
          current.metadataByMerchandise,
          merchandiseId,
          merchandiseDutyId,
          { data: { eventMessage: primaryHtsMetadata } }
        ),
      });
    }

    case 'UPDATE_TARIFFS': {
      const { invoiceId, merchandiseId, updatedDuties } = action.payload || {};
      if (!invoiceId || !merchandiseId) return state;

      const normalizeHts = (htsCode) => {
        if (!htsCode) return '';
        return String(htsCode).replace(/\./g, '').trim();
      };

      const current = state.byInvoiceId[invoiceId] || defaultInvoiceUIState();
      let metadataByMerchandise = { ...current.metadataByMerchandise };
      const existingForMerch = metadataByMerchandise[merchandiseId] || {};
      const newMetadataForMerch = { ...existingForMerch };
      const additionalHtsMetadata = action.additionalHtsMetadata || [];

      (updatedDuties || []).forEach((duty) => {
        const meta = additionalHtsMetadata.find(
          (m) => normalizeHts(m?.htsNo) === normalizeHts(duty?.htsus)
        );
        if (meta && duty.merchandiseDutyId) {
          newMetadataForMerch[duty.merchandiseDutyId] = {
            data: { eventMessage: meta },
          };
        }
      });

      metadataByMerchandise = {
        ...metadataByMerchandise,
        [merchandiseId]: newMetadataForMerch,
      };

      return setInvoiceUIState(state, invoiceId, { metadataByMerchandise });
    }

    default:
      return state;
  }
}

export { invoiceKeyFromInvoice };
